import { UseMutationResult, UseMutationOptions, useMutation } from 'react-query';
import { useDispatch } from 'react-redux';

import axios, { AxiosError } from 'axios';
import { useEffectOnce } from 'usehooks-ts';

import { balanceSuccess } from '../../store/slices/user';
import axiosInstance from '../scripts/axios';
import toast from '../scripts/toast';

export type Variables = {
  playerId: string;
  email: string;
  sessionKey: string;
  balance: number;
};

export type Response = {
  cashBalanceInMinorUnits: number;
};

type UseDepositMutationProps = {
  options?: UseMutationOptions<Response, AxiosError<Error>, Variables>;
};

export default function useDepositMutation({
  options
}: UseDepositMutationProps): UseMutationResult<Response, AxiosError<Error>, Variables> {
  const source = axios.CancelToken.source();

  const dispatch = useDispatch();

  const mutation = useMutation(
    async (variables) => {
      const { data } = await axiosInstance.post<Response>(
        '/deposit',
        {
          sessionKey: variables.sessionKey,
          playerId: variables.playerId,
          email: variables.email,
          amount: 1000000 - variables.balance
        },
        {
          cancelToken: source.token
        }
      );

      return data;
    },
    {
      ...options,
      mutationKey: 'deposit_mutation',
      onSuccess: (data, variables, context) => {
        dispatch(balanceSuccess(data.cashBalanceInMinorUnits));

        if (options?.onSuccess) {
          options.onSuccess(data, variables, context);
        }
      },
      onError: (error, variables, context) => {
        console.error(error);

        toast.error({ title: 'Oh no! 😞', message: 'Failed to deposit!' });

        if (options?.onError) {
          options.onError(error, variables, context);
        }
      }
    }
  );

  useEffectOnce(() => {
    return () => source.cancel();
  });

  return mutation;
}
